import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import {MetadataPanelComponent} from '../../shared/vte';
import {
    AuthenticationSummaryView,
    VisionProviderManagementViewService,
    VisionProviderSubscriptionApiService,
} from '../../services/vision-provider-management';
import {MetadataEntry} from '../../services/vte/vehicle-transaction-view.service';

@Component({
    selector: 'app-provider-authentication',
    standalone: true,
    imports: [CommonModule, MetadataPanelComponent],
    templateUrl: './provider-authentication.component.html',
    styleUrl: './provider-authentication.component.css',
})
export class ProviderAuthenticationComponent implements OnInit, OnDestroy {
    providerId = '';
    entries: MetadataEntry[] = [];
    summary: AuthenticationSummaryView | null = null;
    loading = false;
    connecting = false;

    private routeSub?: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private api: VisionProviderSubscriptionApiService,
        private viewService: VisionProviderManagementViewService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.routeSub = this.route.parent?.paramMap.subscribe((params) => {
            this.providerId = params.get('providerId') || '';
            this.handleOAuthReturnQuery();
            this.loadAuthentication();
        });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
    }

    get oauthConfigured(): boolean {
        return !!this.summary?.oauthConfigured;
    }

    get actionLabel(): string {
        if (this.connecting) {
            return 'Connecting…';
        }
        return this.oauthConfigured ? 'Re-authenticate' : 'Connect';
    }

    startOAuth(): void {
        if (!this.providerId || this.connecting || this.loading) {
            return;
        }
        this.connecting = true;
        this.api.startOAuth(this.providerId).subscribe({
            next: (response) => {
                const url = (response?.authorization_url || '').trim();
                if (!url) {
                    this.connecting = false;
                    this.toastr.error('Unable to start provider authentication.');
                    return;
                }
                // Full-page redirect to the provider authorize URL (no popup/modal).
                window.location.href = url;
            },
            error: (error) => {
                this.connecting = false;
                const detail = error?.error?.detail;
                this.toastr.error(
                    typeof detail === 'string' && detail
                        ? detail
                        : 'Unable to start provider authentication.',
                );
            },
        });
    }

    loadAuthentication(): void {
        if (!this.providerId) {
            return;
        }
        this.loading = true;
        this.viewService.loadProviderSummary(this.providerId).subscribe({
            next: (providerSummary) => {
                this.summary = this.viewService.buildAuthenticationSummary(
                    providerSummary.diagnostics,
                );
                this.entries = this.viewService.buildAuthenticationEntries(
                    providerSummary.diagnostics,
                );
                this.loading = false;
            },
            error: () => {
                this.entries = [];
                this.summary = null;
                this.loading = false;
                this.toastr.error('Unable to load authentication status.');
            },
        });
    }

    private handleOAuthReturnQuery(): void {
        const oauth = this.route.snapshot.queryParamMap.get('oauth');
        if (oauth !== 'success' && oauth !== 'error') {
            return;
        }

        // Clear query first so browser refresh does not re-toast.
        this.clearOAuthQueryParam();

        if (oauth === 'success') {
            this.toastr.success('Provider authentication completed successfully.');
        } else {
            this.toastr.error('Provider authentication failed or was cancelled.');
        }
    }

    private clearOAuthQueryParam(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {oauth: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }
}
